import brand from "../../../brand.config.json";

export const BRAND = brand as { name: string; slug: string };
const ADMIN_TOKEN_KEY = `${BRAND.slug}_admin_token`;
const ADMIN_REFRESH_KEY = `${BRAND.slug}_admin_refresh`;
export const SSO_ATTEMPT_KEY = `${BRAND.slug}_admin_sso_attempted`;

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4000";

type AuthMode = "sso" | "legacy" | null;

/**
 * AAMOS SSO never exposes access or refresh credentials to JavaScript. They
 * live only in Secure HttpOnly API-host cookies. The anti-CSRF value is not an
 * auth credential and is deliberately kept in memory only.
 *
 * The legacy bearer-token path remains as an explicit break-glass fallback.
 */
let accessToken: string | null = sessionStorage.getItem(ADMIN_TOKEN_KEY);
let refreshToken: string | null = sessionStorage.getItem(ADMIN_REFRESH_KEY);
let authMode: AuthMode = accessToken ? "legacy" : null;
let ssoCsrfToken: string | null = null;

interface SsoSessionResponse {
  authenticated: true;
  user: { id: string; email: string; displayName: string; role: "admin" };
  csrfToken: string;
  accessTokenExpiresIn: number;
}

export function setTokens(access: string | null, refresh?: string | null): void {
  accessToken = access;
  authMode = access ? "legacy" : null;
  if (access) sessionStorage.setItem(ADMIN_TOKEN_KEY, access);
  else sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  if (refresh !== undefined) {
    refreshToken = refresh;
    if (refresh) sessionStorage.setItem(ADMIN_REFRESH_KEY, refresh);
    else sessionStorage.removeItem(ADMIN_REFRESH_KEY);
  }
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function readJson<T>(res: Response): Promise<T> {
  return (await res.json().catch(() => ({}))) as T;
}

function acceptSsoSession(session: SsoSessionResponse): boolean {
  if (!session.authenticated || session.user?.role !== "admin" || !session.csrfToken) return false;
  ssoCsrfToken = session.csrfToken;
  authMode = "sso";
  // Remove old break-glass credentials once the safer SSO session works.
  accessToken = null;
  refreshToken = null;
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_REFRESH_KEY);
  sessionStorage.removeItem(SSO_ATTEMPT_KEY);
  return true;
}

async function fetchSsoSession(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/admin/v1/auth/session`, { credentials: "include" });
    return res.ok && acceptSsoSession(await readJson<SsoSessionResponse>(res));
  } catch {
    return false;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForPeerSsoRefresh(): Promise<boolean> {
  for (const waitMs of [75, 150, 300, 600]) {
    await delay(waitMs);
    if (await fetchSsoSession()) return true;
  }
  return false;
}

async function performSsoRefresh(): Promise<boolean> {
  // Another tab may have completed rotation before this tab acquired the
  // browser-wide lock. Re-reading the HttpOnly access cookie is sufficient.
  if (await fetchSsoSession()) return true;

  try {
    const res = await fetch(`${API_BASE}/admin/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (res.ok) return acceptSsoSession(await readJson<SsoSessionResponse>(res));
    if (res.status === 409) return waitForPeerSsoRefresh();
    return false;
  } catch {
    return false;
  }
}

let ssoRefreshInFlight: Promise<boolean> | null = null;

async function trySsoRefresh(): Promise<boolean> {
  if (ssoRefreshInFlight) return ssoRefreshInFlight;
  ssoRefreshInFlight = (async () => {
    try {
      if (typeof navigator !== "undefined" && navigator.locks) {
        return navigator.locks.request(`${BRAND.slug}-admin-sso-refresh`, performSsoRefresh);
      }
      return performSsoRefresh();
    } finally {
      ssoRefreshInFlight = null;
    }
  })();
  return ssoRefreshInFlight;
}

let legacyRefreshInFlight: Promise<boolean> | null = null;

async function tryLegacyRefresh(): Promise<boolean> {
  if (!refreshToken) return false;
  if (legacyRefreshInFlight) return legacyRefreshInFlight;
  legacyRefreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await readJson<{ accessToken?: string; refreshToken?: string }>(res);
      if (!data.accessToken || !data.refreshToken) return false;
      setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      legacyRefreshInFlight = null;
    }
  })();
  return legacyRefreshInFlight;
}

async function tryRefresh(): Promise<boolean> {
  return authMode === "sso" ? trySsoRefresh() : tryLegacyRefresh();
}

export async function initializeAdminAuth(): Promise<boolean> {
  try {
    if (await fetchSsoSession()) return true;
    if (await trySsoRefresh()) return true;
  } catch {
    // A temporary API failure must still leave the explicit local fallback usable.
  }
  authMode = accessToken ? "legacy" : null;
  return authMode !== null;
}

export async function isSsoEnabled(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/admin/v1/auth/status`, { credentials: "include" });
    if (!res.ok) return false;
    const body = await readJson<{ enabled?: boolean }>(res);
    return body.enabled === true;
  } catch {
    return false;
  }
}

/** Preserve an EOC deep link across AAMOS Login without carrying a stale error. */
export function adminReturnToFromHref(href: string): string {
  try {
    const url = new URL(href);
    url.searchParams.delete("ssoError");
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "/";
  }
}

export function beginSso(returnTo: string): void {
  const target = returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/";
  const url = new URL("/admin/v1/auth/oidc/start", API_BASE);
  url.searchParams.set("returnTo", target);
  window.location.assign(url.toString());
}

export async function api<T = unknown>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const hasBody = options.body != null;
  const isMutation = !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
  const doFetch = () =>
    fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers: {
        ...(hasBody ? { "content-type": "application/json" } : {}),
        ...(authMode === "legacy" && accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...(authMode === "sso" && isMutation && path.startsWith("/admin/") && ssoCsrfToken
          ? { "x-csrf-token": ssoCsrfToken }
          : {}),
      },
      body: hasBody ? JSON.stringify(options.body) : undefined,
    });

  let res = await doFetch();
  if (res.status === 401 && (await tryRefresh())) res = await doFetch();
  if (res.status === 403 && authMode === "sso" && isMutation && path.startsWith("/admin/")) {
    const previousCsrf = ssoCsrfToken;
    if ((await fetchSsoSession()) && ssoCsrfToken !== previousCsrf) res = await doFetch();
  }
  const json = await readJson<{ error?: { code?: string; message?: string } } & T>(res);
  if (!res.ok) {
    if (res.status === 401) {
      if (authMode === "legacy") setTokens(null, null);
      else {
        authMode = null;
        ssoCsrfToken = null;
      }
    }
    throw new ApiRequestError(
      res.status,
      json.error?.code ?? "UNKNOWN",
      json.error?.message ?? `HTTP ${res.status}`,
    );
  }
  return json as T;
}

export interface LoginResult {
  totpRequired: boolean;
  preAuthToken?: string;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  // A local login is explicitly a break-glass session, never an SSO upgrade.
  setTokens(null, null);
  const result = await api<{
    accessToken?: string;
    refreshToken?: string;
    user?: { role: string };
    totpRequired?: boolean;
    preAuthToken?: string;
  }>("/v1/auth/login", { method: "POST", body: { email, password } });
  if (result.totpRequired && result.preAuthToken) {
    return { totpRequired: true, preAuthToken: result.preAuthToken };
  }
  if (!result.user || !result.accessToken) {
    throw new ApiRequestError(500, "INTERNAL", "Oväntat svar från inloggningen.");
  }
  if (result.user.role !== "admin") {
    throw new ApiRequestError(403, "FORBIDDEN", "Kontot saknar admin-behörighet.");
  }
  setTokens(result.accessToken, result.refreshToken ?? null);
  return { totpRequired: false };
}

export async function verifyTotp(preAuthToken: string, code: string): Promise<void> {
  const result = await api<{
    accessToken: string;
    refreshToken?: string;
    user: { role: string };
  }>("/v1/auth/totp-verify", { method: "POST", body: { preAuthToken, code } });
  if (result.user.role !== "admin") {
    throw new ApiRequestError(403, "FORBIDDEN", "Kontot saknar admin-behörighet.");
  }
  setTokens(result.accessToken, result.refreshToken ?? null);
}

export async function logoutAdmin(): Promise<void> {
  try {
    if (authMode === "sso") {
      await api("/admin/v1/auth/logout", { method: "POST" });
    } else if (authMode === "legacy" && accessToken) {
      await api("/v1/auth/logout", { method: "POST" });
    }
  } finally {
    setTokens(null, null);
    ssoCsrfToken = null;
    authMode = null;
    sessionStorage.removeItem(SSO_ATTEMPT_KEY);
  }
}
